/**
 * Auto Premium Breakdown Utilities
 *
 * The per-car base premium rule for a ClarionDoor premium breakdown, shared by the
 * account Price Breakdown (calculate-vehicle-premium.ts) and staff-fe's breakdown modal.
 * The policy PDF lambda and the offer PDF still carry their own copies (#35976).
 *
 * Related GitHub issues: #29852 (per_car_total), #32269 (account parity)
 */

/**
 * A single coverage line for a vehicle, as parsed out of a ClarionDoor premium breakdown.
 */
export type PremiumRate = { type: string; amount: number };

/**
 * A raw ClarionDoor premium breakdown map (`it1-COLL`, `it1-per_car_total`, `BI`, ...).
 * Stored records are not guaranteed to hold numbers on every key, so nulls are tolerated.
 */
export type PremiumBreakdownMap = Record<string, number | null | undefined>;

/**
 * Options for the legacy fallback used when a record predates `per_car_total`.
 */
export type CarTotalOptions = {
  /** Whether to drop UM/UIM types from the fallback sum (legacy double-count guard) */
  excludeUmUim?: boolean;
  /** Set of UM/UIM coverage type strings to drop */
  umUimTypes?: Set<string>;
};

/**
 * Extract one car's coverage lines from a raw premium breakdown map.
 *
 * Keys are matched case-insensitively (`it1-`, `IT1-`, ...) and types are normalized to
 * upper case, so `it1-per_car_total` becomes `{ type: 'PER_CAR_TOTAL', amount }`.
 *
 * @param premiumBreakdown - Raw premium breakdown object from ClarionDoor
 * @param carIteration - The car's iteration number (1-indexed)
 * @returns The coverage rates filed under that iteration
 */
export function getCarRatesFromBreakdown(premiumBreakdown: PremiumBreakdownMap, carIteration: number): PremiumRate[] {
  const iterationPrefix = `it${carIteration}-`;
  return Object.entries(premiumBreakdown)
    .filter(([key]) => key.toLowerCase().startsWith(iterationPrefix))
    .map(([key, value]) => ({
      type: key.replace(/^it\d+-/i, '').toUpperCase(),
      amount: typeof value === 'number' ? value : 0
    }));
}

/**
 * The single source of truth for "what does this car's own coverage cost".
 *
 * Uses the rater's `PER_CAR_TOTAL` when the record has one. Policy-level lines such as UM/UIM
 * are filed under a car's iteration but sit outside `per_car_total`, so summing the iteration's
 * line items instead would charge them to that one car (#32269). Records written before
 * `per_car_total` was stored (#29852) fall back to that sum, optionally dropping the UM/UIM
 * types that double-count on those legacy records.
 *
 * Staff's Premium Breakdown modal and the account Price Breakdown resolve the car base through
 * here. The policy PDF lambda and the offer PDF still carry their own copies (#35976).
 *
 * @param rates - Array of coverage rates for the vehicle
 * @param options - Optional configuration for legacy fallback behavior
 * @returns The car's own coverage premium; callers add any shared-cost split themselves
 */
export function resolveCarTotalFromRates(rates: PremiumRate[], options?: CarTotalOptions): number {
  const perCarTotalRate = rates.find((rate) => rate.type?.toUpperCase() === 'PER_CAR_TOTAL');
  if (perCarTotalRate) {
    return perCarTotalRate.amount;
  }

  const { excludeUmUim = false, umUimTypes } = options || {};
  return rates
    .filter((rate) => !(excludeUmUim && umUimTypes?.has(rate.type)))
    .reduce((sum, rate) => sum + (typeof rate.amount === 'number' ? rate.amount : 0), 0);
}

/**
 * `resolveCarTotalFromRates` for callers holding a raw premium breakdown map rather than
 * a parsed rates array.
 *
 * @param premiumBreakdown - Raw premium breakdown object from ClarionDoor
 * @param carIteration - The car's iteration number (1-indexed)
 * @param options - Optional configuration for legacy fallback behavior
 * @returns The car's own coverage premium; callers add any shared-cost split themselves
 */
export function resolveCarTotalFromBreakdown(
  premiumBreakdown: PremiumBreakdownMap,
  carIteration: number,
  options?: CarTotalOptions
): number {
  return resolveCarTotalFromRates(getCarRatesFromBreakdown(premiumBreakdown, carIteration), options);
}
